import { apiFetch } from "@/integrations/backend/client";
import type { UIExercise, UIGrouping } from "./workouts";
import { formatLocalDateToYYYYMMDD } from "@/lib/date-only";
import type { Exercise, Workout, SetDetail } from "@/data/dummyData";
import { getVideoThumbnailUrl } from "@/lib/exercise-media";

export type AssignedWorkoutMeta = {
  id: string;
  name: string;
  duration_minutes: number;
  description: string;
  assigned_to: string;
  date: string | null;
  workout_template_id: string | null;
};

export type CompletedWorkoutData = {
  intensity: number;
  energy: number;
  points: number;
  minutes: number;
  status: "R" | "S" | "T" | "L"; // Ready, Supercharged, Tired, Low
};

export type CompletedWorkoutReviewData = CompletedWorkoutData & {
  id: string;
  rpe: number | null;
  feedback_comments: string;
  created_at: string | null;
  updated_at: string | null;
};

export type WorkoutAssignedExerciseData = {
  id: string;
  exercise_id: string;
  exercise_name: string;
  order: number;
  sets: number;
  reps: number;
  time_seconds: number;
  rest_seconds: number;
  weight_kg: number;
  weight_lbs: number;
  difficulty: string | null;
};

export type WorkoutAssignedGroupingData = {
  id: string;
  order: number;
  sets: number;
  rest_seconds: number;
  exercises: WorkoutAssignedExerciseData[];
};

export type WorkoutAssignedExerciseReviewData = WorkoutAssignedExerciseData & {
  tracked_sets: TrackedSet[];
};

export type WorkoutAssignedGroupingReviewData = Omit<WorkoutAssignedGroupingData, "exercises"> & {
  exercises: WorkoutAssignedExerciseReviewData[];
};

export type WorkoutAssigned = {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  assigned_to: string;
  date: string;
  workout_template_id: string | null;
  category: string;
  completed_workout: CompletedWorkoutData | null;
  groupings: WorkoutAssignedGroupingData[];
};

export type AssignedWorkoutReview = Omit<WorkoutAssigned, "completed_workout" | "groupings"> & {
  completed_workout: CompletedWorkoutReviewData;
  groupings: WorkoutAssignedGroupingReviewData[];
};

export async function fetchAssignedWorkoutGroupings(assignedWorkoutId: string): Promise<UIGrouping[]> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}/groupings`, { method: "GET" });
  if (!res.ok) throw new Error(`assigned_workouts_groupings_failed:${res.status}`);
  return (await res.json()) as UIGrouping[];
}

export async function addExerciseToAssignedWorkout(assignedWorkoutId: string, exerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}/add-exercise`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_add_exercise_failed:${res.status}`);
  return (await res.json()) as { grouping_id: string };
}

export async function reorderAssignedGroupings(assignedWorkoutId: string, groupingIdsInOrder: string[]) {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}/reorder-groupings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ grouping_ids: groupingIdsInOrder }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_reorder_groupings_failed:${res.status}`);
}

export async function reorderAssignedExercises(groupingId: string, exerciseIdsInOrder: string[]) {
  const res = await apiFetch(`/api/v1/workouts/assigned/groupings/${groupingId}/reorder-exercises`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_ids: exerciseIdsInOrder }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_reorder_exercises_failed:${res.status}`);
}

export async function combineAssignedExercises(droppedExerciseId: string, targetExerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/combine`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dropped_exercise: droppedExerciseId, target_exercise: targetExerciseId }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_combine_exercises_failed:${res.status}`);
}

export async function moveAssignedExerciseToGroupingEnd(exerciseId: string, targetGroupingId: string) {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/move-to-grouping-end`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId, target_grouping: targetGroupingId }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_move_exercise_failed:${res.status}`);
}

export async function extractAssignedExerciseToNewGrouping(exerciseId: string, assignedWorkoutId: string, targetIndex: number) {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/extract-to-new-grouping`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ exercise_id: exerciseId, assigned_workout_id: assignedWorkoutId, target_index: targetIndex }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_extract_failed:${res.status}`);
  return (await res.json()) as string;
}

export async function updateAssignedGroupingSets(groupingId: string, sets: number) {
  const res = await apiFetch(`/api/v1/workouts/assigned/groupings/${groupingId}/update-sets`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sets }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_update_grouping_sets_failed:${res.status}`);
}

export async function updateAssignedGroupingRest(groupingId: string, restSeconds: number) {
  const res = await apiFetch(`/api/v1/workouts/assigned/groupings/${groupingId}/update-rest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rest_seconds: restSeconds }),
  });
  if (!res.ok) throw new Error(`assigned_workouts_update_grouping_rest_failed:${res.status}`);
}

export async function updateAssignedExerciseFields(exerciseId: string, patch: Partial<Pick<UIExercise, "sets"|"reps"|"time_seconds"|"rest_seconds"|"weight_kg"|"weight_lbs"|"difficulty">>) {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/${exerciseId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`assigned_workouts_update_exercise_fields_failed:${res.status}`);
}

export async function deleteAssignedExerciseAndCleanup(exerciseId: string) {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/${exerciseId}/delete-and-cleanup`, { method: "POST" });
  if (!res.ok) throw new Error(`assigned_workouts_delete_exercise_cleanup_failed:${res.status}`);
}

export async function getAssignedWorkoutMeta(id: string): Promise<AssignedWorkoutMeta> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${id}/meta`, { method: "GET" });
  if (!res.ok) throw new Error(`assigned_workout_meta_failed:${res.status}`);
  return (await res.json()) as AssignedWorkoutMeta;
}

export async function patchAssignedWorkoutMeta(id: string, patch: Partial<{ name: string; duration_minutes: number; date: string }>): Promise<void> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${id}/meta`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`assigned_workout_patch_failed:${res.status}`);
}

export async function createAssignedWorkout(
  profileId: string,
  date: Date,
  name: string = "Untitled Workout",
  duration: number = 30
): Promise<string> {
  const res = await apiFetch("/api/v1/workouts/assigned", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      duration_minutes: duration,
      profile_id: profileId,
      date: formatLocalDateToYYYYMMDD(date), // YYYY-MM-DD (local-safe)
    }),
  });
  if (!res.ok) throw new Error(`assigned_workout_create_failed:${res.status}`);
  const json = await res.json();
  return json.id as string;
}

export async function listWorkoutAssigned(clientId: string): Promise<WorkoutAssigned[]> {
  const res = await apiFetch(`/api/v1/workouts/assigned?profile_id=${clientId}`, { method: "GET" });
  if (!res.ok) throw new Error(`list_workout_assigned_failed:${res.status}`);
  return (await res.json()) as WorkoutAssigned[];
}

export async function deleteWorkoutAssigned(assignedWorkoutId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete_workout_assigned_failed:${res.status}`);
}

export async function fetchAssignedWorkoutReview(assignedWorkoutId: string): Promise<AssignedWorkoutReview> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}/review`, { method: "GET" });
  if (!res.ok) throw new Error(`assigned_workout_review_failed:${res.status}`);
  return (await res.json()) as AssignedWorkoutReview;
}



export type TrackedSet = {
  id: string;
  workout_assigned_exercise_id: string;
  set_number: number;
  reps: number | null;
  time_seconds: number | null;
  weight_kg: number | null;
  weight_lbs: number | null;
  created_at: string | null;
  updated_at: string | null;
};

export async function fetchTrackedSets(exerciseId: string): Promise<TrackedSet[]> {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/${exerciseId}/tracked-sets`, { method: "GET" });
  if (!res.ok) throw new Error(`tracked_sets_fetch_failed:${res.status}`);
  return (await res.json()) as TrackedSet[];
}

export async function updateTrackedSet(trackedSetId: string, updates: Partial<Pick<TrackedSet, "reps" | "time_seconds" | "weight_kg" | "weight_lbs">>): Promise<TrackedSet> {
  const res = await apiFetch(`/api/v1/workouts/assigned/tracked-sets/${trackedSetId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`tracked_set_update_failed:${res.status}`);
  return (await res.json()) as TrackedSet;
}

export async function deleteTrackedSet(trackedSetId: string): Promise<{ sets: number; deleted_set_number?: number }> {
  const res = await apiFetch(`/api/v1/workouts/assigned/tracked-sets/${trackedSetId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`tracked_set_delete_failed:${res.status}`);
  return (await res.json()) as { sets: number; deleted_set_number?: number };
}

export async function ensureTrackedSets(exerciseId: string): Promise<TrackedSet[]> {
  const res = await apiFetch(`/api/v1/workouts/assigned/exercises/${exerciseId}/tracked-sets/ensure`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`tracked_sets_ensure_failed:${res.status}`);
  return (await res.json()) as TrackedSet[];
}

export type ExerciseDetail = {
  id: string;
  name: string;
  categories: string[];
  primary_muscles: string[];
  secondary_muscles: string[];
  equipment: string[];
  difficulties: string[];
  muscle_actions?: string[];
  instructions: string;
  tips: string;
  programming_use?: string | null;
  is_compound: boolean;
  video_url: string | null;
  image_url: string | null;
  exercise_types: string[];
  exercise_type_roles?: {
    training_category?: string;
    primary?: string;
    secondary?: string[];
  };
};

export type EquipmentDetail = {
  id: string;
  name: string;
  category: string | null;
  description: string | null;
  instructions: string | null;
  is_standard: boolean;
  image_file: string | null;
};

async function fetchExercisesByIds(exerciseIds: string[]): Promise<Map<string, ExerciseDetail>> {
  const res = await apiFetch("/api/v1/exercises", { method: "GET" });
  if (!res.ok) throw new Error(`exercises_fetch_failed:${res.status}`);
  const allExercises = (await res.json()) as ExerciseDetail[];
  const exerciseMap = new Map<string, ExerciseDetail>();
  for (const ex of allExercises) {
    if (exerciseIds.includes(ex.id)) {
      exerciseMap.set(ex.id, ex);
    }
  }
  return exerciseMap;
}

async function fetchEquipmentByIds(equipmentNames: string[]): Promise<Map<string, EquipmentDetail>> {
  const res = await apiFetch("/api/v1/equipment", { method: "GET" });
  if (!res.ok) throw new Error(`equipment_fetch_failed:${res.status}`);
  const allEquipment = (await res.json()) as EquipmentDetail[];
  const equipmentMap = new Map<string, EquipmentDetail>();
  for (const eq of allEquipment) {
    if (equipmentNames.includes(eq.name)) {
      equipmentMap.set(eq.name, eq);
    }
  }
  return equipmentMap;
}

function transformWorkoutAssignedToWorkout(
  meta: AssignedWorkoutMeta,
  groupings: UIGrouping[],
  exercisesMap: Map<string, ExerciseDetail>,
  equipmentMap: Map<string, EquipmentDetail>,
  trackedSetsMap: Map<string, TrackedSet[]>
): Workout {
  const exercises: Exercise[] = [];
  const exerciseGroups: Workout["exerciseGroups"] = [];

  for (const grouping of groupings) {
    const groupSize = grouping.exercises.length;
    const groupLabel = groupSize >= 3 ? "Circuit" : groupSize === 2 ? "Superset" : "Single";
    const groupExercises: Exercise[] = [];

    for (const [exerciseIndex, workoutExercise] of grouping.exercises.entries()) {
      const exerciseDetail = exercisesMap.get(workoutExercise.exercise);
      if (!exerciseDetail) continue;

      // Use WorkoutAssignedExercise ID (workoutExercise.id) to fetch TrackedSets
      const trackedSets = trackedSetsMap.get(workoutExercise.id) || [];
      const trackedSetsBySetNumber = new Map(trackedSets.map(ts => [ts.set_number, ts]));

      const equipment = exerciseDetail.equipment.filter(Boolean);
      const equipmentDetails = equipment.flatMap((equipmentName) => {
        const instructions = equipmentMap.get(equipmentName)?.instructions?.trim();
        if (!instructions) return [];
        return [{ name: equipmentName, instructions }];
      });

      const groupRounds = grouping.sets || 0;
      const rawExerciseSets = workoutExercise.sets || 0;
      const setsPerRound = groupLabel === "Single"
        ? (rawExerciseSets || groupRounds || 0)
        : (rawExerciseSets > 1 ? rawExerciseSets : 1);
      const totalTrackedSets = groupLabel === "Single"
        ? (rawExerciseSets || groupRounds || 0)
        : setsPerRound * groupRounds;

      // Build setDetails from TrackedSets, using workout_assigned_exercise values as initial defaults
      const setDetails: (SetDetail & { trackedSetId?: string })[] = [];
      for (let setNum = 1; setNum <= totalTrackedSets; setNum++) {
        const trackedSet = trackedSetsBySetNumber.get(setNum);
        // Use trackedSet values if they exist, otherwise fall back to workout_assigned_exercise values
        setDetails.push({
          set: setNum,
          weight: trackedSet?.weight_lbs ?? (workoutExercise.weight_lbs ?? undefined),
          reps: trackedSet?.reps ?? (workoutExercise.reps ?? undefined),
          duration: trackedSet?.time_seconds ?? (workoutExercise.time_seconds ?? undefined),
          trackedSetId: trackedSet?.id,
        });
      }
      
      // Determine which tracking fields are available
      // Check if workoutAssignedExercise has non-null, non-zero values OR if any trackedSet has non-null, non-zero values
      // This ensures fields show even if workoutAssignedExercise is null but trackedSets have values
      // Also excludes 0 values (treating them as "not set")
      const hasRepsFromExercise = workoutExercise.reps != null && workoutExercise.reps !== 0;
      const hasWeightFromExercise = workoutExercise.weight_lbs != null && workoutExercise.weight_lbs !== 0;
      const hasTimeFromExercise = workoutExercise.time_seconds != null && workoutExercise.time_seconds !== 0;
      
      const hasRepsFromTrackedSets = trackedSets.some(ts => ts.reps != null && ts.reps !== 0);
      const hasWeightFromTrackedSets = trackedSets.some(ts => ts.weight_lbs != null && ts.weight_lbs !== 0);
      const hasTimeFromTrackedSets = trackedSets.some(ts => ts.time_seconds != null && ts.time_seconds !== 0);

      const hasReps = hasRepsFromExercise || hasRepsFromTrackedSets;
      const hasWeight = hasWeightFromExercise || hasWeightFromTrackedSets;
      const hasTime = hasTimeFromExercise || hasTimeFromTrackedSets;

      const exercise = {
        id: workoutExercise.id, // Use WorkoutAssignedExercise ID for uniqueness
        name: exerciseDetail.name,
        equipment,
        equipmentImage: exerciseDetail.image_url || "",
        equipmentDetails,
        muscleGroup: exerciseDetail.primary_muscles[0] || "",
        // Prefer the video's thumbnail when there's a video; otherwise show the exercise image.
        videoThumbnail: (exerciseDetail.video_url ? getVideoThumbnailUrl(exerciseDetail.video_url) : null) || exerciseDetail.image_url || "",
        videoUrl: exerciseDetail.video_url,
        imageUrl: exerciseDetail.image_url,
        sets: totalTrackedSets,
        reps: hasReps ? workoutExercise.reps.toString() : "",
        restTime: workoutExercise.rest_seconds != null ? workoutExercise.rest_seconds.toString() : "",
        weight: hasWeight ? workoutExercise.weight_lbs.toString() : "",
        instructions: exerciseDetail.instructions || "",
        completed: false,
        completedSets: 0,
        setDetails,
        grouping: groupLabel === "Single" ? undefined : {
          id: grouping.id,
          label: groupLabel,
          size: groupSize,
          exerciseIndex,
          rounds: groupRounds,
          setsPerRound,
          restSeconds: grouping.rest_seconds || 0,
        },
        // Store tracking field availability as metadata
        _hasReps: hasReps,
        _hasWeight: hasWeight,
        _hasTime: hasTime,
        // Store WorkoutAssignedExercise ID for fetching TrackedSets
        _workoutAssignedExerciseId: workoutExercise.id,
      } as Exercise & { _hasReps?: boolean; _hasWeight?: boolean; _hasTime?: boolean; _workoutAssignedExerciseId?: string };

      groupExercises.push(exercise);
      exercises.push(exercise);
    }

    exerciseGroups?.push({
      id: grouping.id,
      label: groupLabel,
      rounds: grouping.sets || 0,
      restSeconds: grouping.rest_seconds || 0,
      exercises: groupExercises,
    });
  }

  return {
    id: meta.id,
    title: meta.name,
    description: meta.description || "",
    difficulty: "",
    duration: meta.duration_minutes ? `${meta.duration_minutes} min` : "",
    category: "",
    totalExercises: exercises.length,
    exercises,
    exerciseGroups,
  };
}

export async function fetchAssignedWorkoutFull(assignedId: string): Promise<Workout> {
  // Fetch workout meta
  const meta = await getAssignedWorkoutMeta(assignedId);
  
  // Fetch groupings
  const groupings = await fetchAssignedWorkoutGroupings(assignedId);
  
  // Collect all exercise IDs and equipment names
  const exerciseIds: string[] = [];
  const equipmentNames: string[] = [];
  for (const grouping of groupings) {
    for (const workoutExercise of grouping.exercises) {
      exerciseIds.push(workoutExercise.exercise);
    }
  }
  
  // Fetch exercise details and equipment details in parallel
  const [exercisesMap, allExercises] = await Promise.all([
    fetchExercisesByIds(exerciseIds),
    apiFetch("/api/v1/exercises", { method: "GET" }).then(res => res.json() as Promise<ExerciseDetail[]>),
  ]);
  
  // Extract equipment names from exercises
  for (const ex of allExercises) {
    if (exerciseIds.includes(ex.id)) {
      for (const eqName of ex.equipment) {
        if (!equipmentNames.includes(eqName)) {
          equipmentNames.push(eqName);
        }
      }
    }
  }
  
  const equipmentMap = await fetchEquipmentByIds(equipmentNames);
  
  // Fetch TrackedSets for each WorkoutAssignedExercise (not Exercise)
  const trackedSetsMap = new Map<string, TrackedSet[]>();
  const workoutAssignedExerciseIds: string[] = [];
  for (const grouping of groupings) {
    for (const workoutExercise of grouping.exercises) {
      workoutAssignedExerciseIds.push(workoutExercise.id); // This is the WorkoutAssignedExercise ID
    }
  }
  
  await Promise.all(
    workoutAssignedExerciseIds.map(async (workoutAssignedExerciseId) => {
      try {
        const trackedSets = await fetchTrackedSets(workoutAssignedExerciseId);
        trackedSetsMap.set(workoutAssignedExerciseId, trackedSets);
      } catch (error) {
        console.error(`Failed to fetch tracked sets for workout assigned exercise ${workoutAssignedExerciseId}:`, error);
        trackedSetsMap.set(workoutAssignedExerciseId, []);
      }
    })
  );
  
  // Transform to Workout format
  return transformWorkoutAssignedToWorkout(meta, groupings, exercisesMap, equipmentMap, trackedSetsMap);
}

export type CompleteAssignedWorkoutPayload = Partial<{
  rpe: number;
  feedback_comments: string;
  intensity: number;
  energy: number;
  points: number;
  minutes: number;
  status: "R" | "S" | "T" | "L";
}>;

export async function completeAssignedWorkout(
  assignedWorkoutId: string,
  payload: CompleteAssignedWorkoutPayload
): Promise<{ id: string; workout_assigned_id: string }> {
  const res = await apiFetch(`/api/v1/workouts/assigned/${assignedWorkoutId}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`assigned_workout_complete_failed:${res.status}`);
  return (await res.json()) as { id: string; workout_assigned_id: string };
}
